import React, { useEffect, useState } from "react";
import PricingPlanView from "./PricingPlanView";
import {
	addCardApi,
	cancelPlanApi,
	defaultCardApi,
	deleteCardApi,
	subscribePlanApi,
	useGetPaymentMethods,
	useGetPricingPlans,
	createFinancialConnection,
	attachBankAccount,
} from "services/CompanyService";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../redux/reducers/LoaderSlice";
import { toast } from "react-toastify";
import { formatDateToCustomString } from "utils/helpers/Common";
import { BRAND_US_BANK_ACCOUNT } from "utils/constants/Constants";
import { useStripe } from "@stripe/react-stripe-js";

const PricingPlan = ({ company, searchParams, setSearchParams, setSuccessAlert, showManageCard = true, showFooterText = true, handleBack, handleContinue, isOnboarding = false }) => {
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [cancelPlan, setCancelPlan] = useState(false);
	const [changePlan, setChangePlan] = useState(null);
	const [changeAlert, setChangeAlert] = useState({ type: null, message: null });
	const [addCard, setAddCard] = useState(false);
	const { plans, plansMutate } = useGetPricingPlans(company);
	const selectedCustomPlan = plans?.find((plan) => plan.is_custom && plan.selected);
	const selectedPlanWithNextCustom = plans?.find((plan) => plan.selected && plan.next_plan?.is_custom);
	const nonCustomPlans = plans?.filter((plan) => !plan.is_custom);
	const nextCustomPlan = selectedPlanWithNextCustom ? plans?.find((plan) => plan.id === selectedPlanWithNextCustom.next_plan.id) : null;
	const filteredPlans = selectedCustomPlan
		? [selectedCustomPlan, ...nonCustomPlans]
		: selectedPlanWithNextCustom && nextCustomPlan
		? [...nonCustomPlans, { ...nextCustomPlan, starts_at: selectedPlanWithNextCustom.ends_at }]
		: nonCustomPlans;
	const { cards, cardsMutate } = useGetPaymentMethods(company);
	const dispatch = useDispatch();

	const [achAccounts, setAchAccounts] = useState([]);
	const [creditCards, setCreditCards] = useState([]);
	const [anyPlanHasAch, setAnyPlanHasAch] = useState(false);
	const stripe = useStripe();

	useEffect(() => {
		if (cards?.length > 0) {
			setAchAccounts(cards.filter((card) => card.brand === BRAND_US_BANK_ACCOUNT));
			setCreditCards(cards.filter((card) => card.brand !== BRAND_US_BANK_ACCOUNT));
		}
	}, [cards]);

	useEffect(() => {
		if (plans?.length > 0) {
			const currentPlan = plans.find((plan) => plan.selected == true);
			if (currentPlan) {
				setSelectedPlan(currentPlan);
				if (searchParams?.get("pricing_plan") && searchParams?.get("success") == 1 && currentPlan?.id == searchParams.get("plan_id")) {
					setSuccessAlert(true);
					setSearchParams([]);
				}
			} else {
				setSelectedPlan(company?.tier);
			}
			setAnyPlanHasAch(plans.some((plan) => plan.next_ach_plan));
		}
	}, [plans]);

	const handleChoosePlan = (plan, resubscribe = false) => {
		dispatch(showLoader());
		subscribePlanApi({
			plan_id: plan.id,
			company_id: company.id,
			upgrade: plan?.price > selectedPlan?.price ? true : false,
			downgrade: plan?.price < selectedPlan?.price ? true : false,
			is_onboarding: isOnboarding,
			resubscribe,
		})
			.then((res) => {
				if (res?.data?.url) window.location.href = res.data.url;
				else {
					const paymentMethodType = res?.data?.data?.paymentMethodType;
					if (paymentMethodType !== BRAND_US_BANK_ACCOUNT && selectedPlan.price !== 0 && changePlan.price !== 0) {
						setChangeAlert({
							type: resubscribe ? "resubscribe" : "change",
							message: resubscribe
								? "Your Package has been resubscribed successfully."
								: `You have successfully unsubscribed from your current plan 
								   which ends on ${formatDateToCustomString(selectedPlan.ends_at)}. 
								   Your new plan will start at the beginning of your next billing cycle, 
								   ${formatDateToCustomString(selectedPlan.ends_at, 1)}.`,
						});
					}
					plansMutate();
				}
			})
			.catch((err) => {
				toast.error(err.response.data.message);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const handleCancelPlan = () => {
		dispatch(showLoader());
		cancelPlanApi(company.id, {
			company_id: company.id,
			plan_id: selectedPlan.id,
		})
			.then((res) => {
				setCancelPlan(false);
				setChangeAlert({
					type: "cancel",
					message: `   You have successfully unsubscribed from your current plan
                  which ends on
                  ${formatDateToCustomString(selectedPlan?.ends_at)}. Your
                  new plan will default to Plan 1 under which you will be
                  charged per project. You will still be able to access your
                  account and view existing projects. If you subscribe to a new
                  plan, it will start at the beginning of your next billing
                  cycle.`,
				});
				plansMutate();
			})
			.catch((err) => {
				toast.error(err.response.data.message);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const handleDefaultCard = (cardId) => {
		dispatch(showLoader());
		defaultCardApi(company.id, cardId)
			.then((res) => {
				toast.success(res.data.message);
				cardsMutate();
			})
			.catch((err) => {
				toast.error(err.response.data.message);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const handleDeleteCard = (cardId) => {
		dispatch(showLoader());
		deleteCardApi(cardId, company.id)
			.then((res) => {
				toast.success(res.data.message);
				cardsMutate();
			})
			.catch((err) => {
				toast.error(err.response.data.message);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const handleAddCard = (data) => {
		dispatch(showLoader());
		addCardApi(company.id, data)
			.then((res) => {
				toast.success(res.data.message);
				setAddCard(false);
				cardsMutate();
			})
			.catch((err) => {
				toast.error(err.response.data.message);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const handleAddACH = async () => {
		try {
			// Step 1: Create financial account and setup intent
			const { intentSecret, financialAccountSecret } = await createFinancialAccountAndSetupIntent();
			if (!intentSecret || !financialAccountSecret) return;

			// Step 2: Collect financial account from Stripe
			const bankAccountData = await collectStripeFinancialConnectionsAccounts(financialAccountSecret);
			if (bankAccountData?.financialAccountId === null) {
				return;
			}

			// Step 3: Attach the bank account to the customer
			const paymentMethodId = await attachBankAccountToCustomer(bankAccountData);
			if (!paymentMethodId) return;

			// Step 4: Confirm the setup intent
			await confirmSetupIntent(intentSecret, paymentMethodId);

			setSearchParams([]);
		} catch (error) {
			console.error("Error in handleAddACH:", error);
			toast.error("An error occurred while adding the bank account.");
		}
	};

	const createFinancialAccountAndSetupIntent = async () => {
		try {
			dispatch(showLoader());
			const response = await createFinancialConnection(company?.id);
			const setupIntentClientSecret = response?.data?.data?.setupIntentClientSecret;
			const financialConnectionsClientSecret = response?.data?.data?.financialConnectionsClientSecret;

			if (!setupIntentClientSecret || !financialConnectionsClientSecret) {
				throw new Error("Missing required client secrets.");
			}

			return {
				intentSecret: setupIntentClientSecret,
				financialAccountSecret: financialConnectionsClientSecret,
			};
		} catch (error) {
			toast.error(error.response?.data?.message || "Something went wrong, please try again.");
			return { intentSecret: null, financialAccountSecret: null };
		} finally {
			dispatch(hideLoader());
		}
	};

	const collectStripeFinancialConnectionsAccounts = async (financialAccountSecret) => {
		try {
			const { financialConnectionsSession } = await stripe.collectFinancialConnectionsAccounts({
				clientSecret: financialAccountSecret,
				params: { payment_method_type: "us_bank_account" },
			});

			const financialAccount = financialConnectionsSession?.accounts?.find((acc) => acc.status === "active");
			if (!financialAccount) {
				financialConnectionsSession?.accounts?.length && toast.error("No active financial account found.");
				return {
					financialAccountId: null,
					companyId: company?.id,
				};
			}

			return {
				financialAccountId: financialAccount?.id,
				companyId: company?.id,
			};
		} catch (error) {
			toast.error("Something went wrong, please try again.");
			return null;
		}
	};

	const attachBankAccountToCustomer = async (accountData) => {
		try {
			dispatch(showLoader());
			const response = await attachBankAccount(accountData);
			const paymentMethodId = response?.data?.data?.payment_method_id;

			if (!paymentMethodId) {
				throw new Error("Failed to attach bank account.");
			}

			return paymentMethodId;
		} catch (error) {
			toast.error(error.response?.data?.message || "Something went wrong, please try again.");
			return null;
		} finally {
			dispatch(hideLoader());
		}
	};

	const confirmSetupIntent = async (intentSecret, paymentMethodId) => {
		try {
			const { error } = await stripe.confirmSetup({
				clientSecret: intentSecret,
				confirmParams: {
					payment_method: paymentMethodId,
					return_url: window.location.href,
				},
			});
			if (error) {
				throw new Error(error.message);
			}
		} catch (error) {
			toast.error("Setup Intent confirmation failed");
		}
	};

	return (
		<PricingPlanView
			plans={filteredPlans}
			handleChoosePlan={handleChoosePlan}
			company={company}
			selectedPlan={selectedPlan}
			handleCancelPlan={handleCancelPlan}
			cancelPlan={cancelPlan}
			setCancelPlan={setCancelPlan}
			changePlan={changePlan}
			setChangePlan={setChangePlan}
			handleDefaultCard={handleDefaultCard}
			handleDeleteCard={handleDeleteCard}
			addCard={addCard}
			setAddCard={setAddCard}
			handleAddCard={handleAddCard}
			changeAlert={changeAlert}
			setChangeAlert={setChangeAlert}
			showManageCard={showManageCard}
			showFooterText={showFooterText}
			handleBack={handleBack}
			handleContinue={handleContinue}
			creditCards={creditCards}
			achAccounts={achAccounts}
			anyPlanHasAch={anyPlanHasAch}
			handleAddACH={handleAddACH}
		/>
	);
};

export default PricingPlan;
